/* Whether the API is reachable, shared by the API client (which reports it)
   and MaintenanceScreen (which shows it). A tiny store instead of Redux so the
   axios interceptor can update it without a dispatch. */

let apiDown = false;
const listeners = new Set();

const setApiDown = (value) => {
  if (apiDown === value) return;
  apiDown = value;
  listeners.forEach((listener) => listener());
};

export const subscribeApiStatus = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const isApiDown = () => apiDown;

/* Gateway answers mean the server behind it is down or being deployed. */
const DOWN_STATUSES = [502, 503, 504];

const isNetworkError = (error) =>
  !error?.response && error?.code === "ERR_NETWORK";

/* True when the API itself cannot be reached. A request that was cancelled,
   or that got any normal HTTP answer (400, 401, 404, 500…), is not that. */
export const isApiDownError = (error) =>
  isNetworkError(error) ||
  DOWN_STATUSES.includes(error?.response?.status);

/* Is the server answering at all? `no-cors` gets an opaque response from any
   live server — no CORS headers or login needed — and rejects only when
   nothing answers. A 502/503 from a gateway still counts as down. */
export const pingApi = async (baseURL) => {
  if (!baseURL) return true;
  try {
    const response = await fetch(baseURL, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
    });
    return !DOWN_STATUSES.includes(response.status);
  } catch (error) {
    return false;
  }
};

let confirming = false;

/* One failed request can be a blip, so a network error is confirmed with a
   ping before the maintenance screen covers the app. A gateway 502/503/504
   already is that confirmation. */
export const reportApiError = async (error, baseURL) => {
  if (!isApiDownError(error)) {
    if (error?.response) setApiDown(false);
    return;
  }
  if (!isNetworkError(error)) {
    setApiDown(true);
    return;
  }
  if (confirming || apiDown) return;
  confirming = true;
  try {
    if (!(await pingApi(baseURL))) setApiDown(true);
  } finally {
    confirming = false;
  }
};

export const reportApiUp = () => setApiDown(false);
